import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ListItemCard, Text, UnstyledButton } from '@/shared/ui';
import { ChevronDownIcon } from '@/shared/ui/radix-icons';
import { cn } from '@/shared/ui/utils/tailwind';
import { useDealStageLabels, type DealStage } from '@features/deals/constants/deal-stages';
import {
  ACCENT_SOFT_BG_CLASS,
  ACCENT_TEXT_CLASS,
  IDLE_TONE_COLOR,
  daysSince,
  dealAccentForStage,
  idleToneFor,
} from '@features/deals/utils/deal-state';
import type { Attendance } from '../hooks/useAttendances';

export interface AttendanceCardProps {
  agencyId: string;
  attendance: Attendance;
  /**
   * Turns the stage pill into a button that opens the move sheet.
   *
   * Only the phone passes it. On desktop the stage moves by dragging the
   * whole card, and a second way to do the same thing — one of them hidden
   * inside the drag handle — would be a trap: a press that was meant to
   * start a drag would open a sheet instead.
   */
  onMoveStage?: () => void;
}

/**
 * One attendance on the board: who, which property, and the stage.
 *
 * The stage is shown because the column is a group of stages — without
 * it, two cards in "Conversando" look identical when one is only
 * qualified and the other already has a visit booked. It is a tinted
 * pill rather than a third line of prose: as plain text at the same
 * size and tone as the property line, "Fechamento" read as part of the
 * property's name instead of as the attendance's status. The pill's
 * accent comes from the same stage→accent rule the detail screen uses,
 * so opening a card cannot change the colour its stage was wearing.
 *
 * The property title is line-clamped: these come from the crawler and
 * run to a full sentence ("Apartamento para comprar com 64 m², 2
 * quartos, 3 banheiros, 1 vaga em Jacarepaguá, Rio de Janeiro"), which
 * uncapped is the tallest thing on the card and buries the client's
 * name it is supposed to qualify.
 *
 * There is no per-card action: the card opens the attendance, and every
 * action lives on its detail, where the stage rules are enforced. The
 * previous card carried a temperature action that could disagree with
 * the deal it belonged to.
 *
 * Under the stage sits how long the attendance has stood still, which is
 * what decides whether it needs the broker today — the stage says where
 * it is, not whether it is moving, and a board of stages alone reads the
 * same on a card touched this morning and one untouched since August.
 * The threshold and the colours come from `deal-state`, so the board and
 * the detail cannot drift to different definitions of stalled.
 */
export function AttendanceCard({ agencyId, attendance, onMoveStage }: AttendanceCardProps) {
  const { t } = useTranslation('features/attendances');
  const navigate = useNavigate();
  const stageLabels = useDealStageLabels();
  const accent = dealAccentForStage(attendance.stage);
  const idleDays = daysSince(attendance.lastEventAt);
  const idleTone = idleToneFor(idleDays);

  return (
    <div
      data-testid="attendance-card"
      data-stage={attendance.stage}
      draggable
      // The id travels on the drag, not in a ref: a card dropped on a
      // column the pointer left and re-entered must still say which
      // attendance it is.
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', attendance.dealId);
        // The stage rides along so the board can tell "this one is
        // closed" from "it cannot go there yet" -- the server answers
        // both in the same error class.
        event.dataTransfer.setData('application/x-attendance-stage', attendance.stage);
        event.dataTransfer.effectAllowed = 'move';
      }}
      className="cursor-grab active:cursor-grabbing"
    >
      <ListItemCard
        accentColor={`var(--accent-${accent})`}
        firstName={attendance.clientName || t('card.unnamedClient')}
        description={attendance.propertyTitle || t('card.noProperty')}
        descriptionClamp={2}
        onClick={() => navigate(`/app/agencies/${agencyId}/deals/${attendance.dealId}`)}
        footer={onMoveStage ? (
          /*
           * The pill is the control, and it sits outside the card's own click
           * target: the card opens the attendance and this moves it, and the
           * two must not be reachable by the same tap. 32px tall so a thumb
           * can hit it without hitting the card underneath.
           */
          <UnstyledButton
            data-testid="attendance-card-stage"
            aria-label={t('move.open', {
              stage: stageLabels[attendance.stage as DealStage] ?? attendance.stage,
            })}
            onClick={(event) => {
              event.stopPropagation();
              onMoveStage();
            }}
            className={cn(
              'flex h-8 items-center gap-(--space-1) self-start rounded-full px-(--space-3)',
              'text-[length:var(--text-eyebrow)] tracking-(--tracking-chip)',
              '[font-weight:var(--font-semibold)]',
              ACCENT_SOFT_BG_CLASS[accent],
              ACCENT_TEXT_CLASS[accent],
            )}
          >
            {stageLabels[attendance.stage as DealStage] ?? attendance.stage}
            <ChevronDownIcon width={12} height={12} aria-hidden="true" />
          </UnstyledButton>
        ) : undefined}
      >
        {!onMoveStage && (
          <Text
            component="span"
            data-testid="attendance-card-stage"
            className={cn(
              'self-start rounded-full px-(--space-2) py-(--space-0-5)',
              'text-[length:var(--text-eyebrow)] tracking-(--tracking-chip)',
              ACCENT_SOFT_BG_CLASS[accent],
              ACCENT_TEXT_CLASS[accent],
            )}
          >
            {stageLabels[attendance.stage as DealStage] ?? attendance.stage}
          </Text>
        )}

        {idleTone && (
          <Text
            component="span"
            data-testid="attendance-card-idle"
            data-idle-tone={idleTone}
            style={{ color: IDLE_TONE_COLOR[idleTone] }}
            className={cn(
              'flex items-center gap-(--space-1-5)',
              'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]',
            )}
          >
            <div
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: IDLE_TONE_COLOR[idleTone] }}
            />
            {idleDays === 0
              ? t('card.movedToday')
              : t('card.idleDays', { count: idleDays as number })}
          </Text>
        )}
      </ListItemCard>
    </div>
  );
}

export default AttendanceCard;
